import { useState, useEffect, useMemo } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { debounce, isEmpty } from 'lodash';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Tab from '@mui/material/Tab';
import { useTheme } from '@mui/material/styles';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import { tabsSx, tabPanelSx, customReportTabGridSx, noSearchResultsSx, searchBarInlineSx } from '../styles';
import SearchBar from '../../../components/GlobalFilterBar/SearchBar/SearchBar';
import SelectionList from '../../../components/SelectionList/SelectionList';
import { SelectedUser, UserGroupType } from './PublishReportDialog';
import UserSelectionAccordion from './UserSelectionAccordion';
import { formatSelection } from '../utils/PublishReportUtils';

interface UserOptionItem {
  id: string;
  label: string | React.ReactElement;
  userName?: string;
  displayName?: string;
  roleName?: string;
  users?: string[];
}

interface UserOptions {
  id: string;
  label: string;
  options: UserOptionItem[];
}

interface SelectUsersProps {
  selectedOptions: SelectedUser[];
  setSelectedOptions: (opts: SelectedUser[]) => void;
  userOptions: UserOptions[];
  onOptionToggle?: (groupId: string, optionId: string, optionLabel: string, userName?: string) => void;
  onSelectAllToggle?: (
    groupId: string,
    allIds: { id: string; name: string }[],
    isCurrentlyAllSelected: boolean
  ) => void;
  onRemoveItem?: (itemId: string) => void;
  onClearAll?: () => void;
}

/* Main component for selecting users */
function SelectUsers({
  selectedOptions,
  setSelectedOptions,
  userOptions,
  onOptionToggle,
  onSelectAllToggle,
  onRemoveItem,
  onClearAll,
}: Readonly<SelectUsersProps>) {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState('1');

  const { INDIVIDUALS, ROLES } = UserGroupType;

  // Shared search state for both tabs
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Debounce search input
  const debounceSearchFn = useMemo(
    () => debounce((value: string) => setDebouncedSearchText(value?.trim() || ''), 300),
    []
  );

  // Cleanup debounce on unmount
  useEffect(() => () => debounceSearchFn.cancel(), [debounceSearchFn]);

  // Return true if all options are selected for a user group
  const isAllSelected = (groupId: string, allIds: string[]) => {
    const selectedInGroup = selectedOptions.filter((item) => item.groupType === groupId);
    return selectedInGroup.length === allIds.length;
  };

  // toggle the Select All switch for a user group
  const toggleSelectAll = (groupId: string, allIds: { id: string; name: string; userName?: string }[]) => {
    const isCurrentlyAllSelected = isAllSelected(
      groupId,
      allIds.map((o) => o.id)
    );

    // If custom handler is provided, use it (for manage mode with confirmation)
    if (onSelectAllToggle) {
      onSelectAllToggle(groupId, allIds, isCurrentlyAllSelected);
      return;
    }

    // Default toggle behavior
    if (isCurrentlyAllSelected) {
      // Deselect all from this specific group only
      setSelectedOptions(selectedOptions.filter((item) => item.groupType !== groupId));
      return;
    }
    const all = allIds.map((option) => formatSelection(groupId, option.id, option.name, option.userName));
    // Remove existing selections from this group and add the new ones
    const otherGroupSelections = selectedOptions.filter((item) => item.groupType !== groupId);
    setSelectedOptions([...otherGroupSelections, ...all]);
  };

  // toggle individual option selection for a user
  const toggleOption = (groupId: string, optionId: string, optionLabel: string, userName?: string) => {
    // If custom handler is provided, use it (for manage mode with confirmation)
    if (onOptionToggle) {
      onOptionToggle(groupId, optionId, optionLabel, userName);
      return;
    }

    // Default toggle behavior
    // generate unique ids while formatting data
    const formatted = formatSelection(groupId, optionId, optionLabel, userName);
    const exists = selectedOptions.some((item) => item.id === formatted.id);
    if (exists) {
      setSelectedOptions(selectedOptions.filter((item) => item.id !== formatted.id));
    } else {
      setSelectedOptions([...selectedOptions, formatted]);
    }
  };

  // Utility function to extract option name from UserOptionItem
  const getOptionName = (option: UserOptionItem): string =>
    option.displayName || (typeof option.label === 'string' ? option.label : option.id);

  // Utility function to map options to select all format
  const mapOptionsForSelectAll = (options: UserOptionItem[]): { id: string; name: string; userName?: string }[] =>
    options.map((o) => ({
      id: o.id,
      name: getOptionName(o),
      userName: o.userName,
    }));

  // Shared function to filter user options by search term
  const filterUserOptionsBySearch = (options: UserOptions[]): UserOptions[] =>
    options
      .map((ds) => {
        const filteredOptions = ds.options.filter((opt: UserOptionItem) => {
          // For React elements, we need to extract the text content for search
          if (typeof opt.label === 'object' && opt.displayName) {
            return (
              opt.displayName.toLowerCase().includes(debouncedSearchText.toLowerCase()) ||
              opt.userName?.toLowerCase().includes(debouncedSearchText.toLowerCase())
            );
          }
          if (typeof opt.label === 'string') {
            return opt.label.toLowerCase().includes(debouncedSearchText.toLowerCase());
          }
          return false;
        });
        return { ...ds, options: filteredOptions };
      })
      .filter((ds) => ds.options.length > 0 || isEmpty(debouncedSearchText));

  // Filter user options by search for Individuals tab

  // Utility function to filter user options by group type
  const filterUserOptionsByGroupType = (filteredUserOptions: UserOptions[]) => {
    const individualOptions = filteredUserOptions.filter((group) => group.id === INDIVIDUALS);
    const roleOptions = filteredUserOptions.filter((group) => group.id === ROLES);
    return { individualOptions, roleOptions };
  };

  const filteredUserOptions = filterUserOptionsBySearch(userOptions);
  const { individualOptions: filteredIndividualsOptions, roleOptions: filteredRolesOptions } =
    filterUserOptionsByGroupType(filteredUserOptions);

  // Function to get chip icon for user role
  const getChipIcon = (item: { groupType?: string }) =>
    item.groupType === ROLES ? <WorkspacesIcon fontSize='small' /> : undefined;

  // Check if no results found for each tab
  const hasNoIndividualsResults = filteredIndividualsOptions?.[0]?.options?.length === 0;
  const hasNoRolesResults = filteredRolesOptions?.[0]?.options?.length === 0;

  // Utility function to handle chip removal
  const handleChipRemoval = (itemId: string) => {
    if (onRemoveItem) {
      onRemoveItem(itemId); // Use parent callback in manage mode
    } else {
      // Default behavior for publish mode
      const itemToRemove = selectedOptions.find((opt) => opt.id === itemId);
      if (itemToRemove) {
        setSelectedOptions(selectedOptions.filter((item) => item.id !== itemId));
      }
    }
  };

  // clear all selections
  const handleClearSelection = () => {
    if (onClearAll) {
      onClearAll(); // Use parent callback in manage mode
    } else {
      setSelectedOptions([]); // Default for publish mode
    }
  };

  // Format selected options for SelectionList component
  const selectedItemsForList = selectedOptions.map((opt) => ({
    id: opt.id,
    name: opt.name,
    groupType: opt.groupType,
    userName: opt.userName,
  }));

  return (
    <TabContext value={tabValue}>
      <Box>
        <TabList
          onChange={(_, newValue: string) => {
            setTabValue(newValue);
            // Clear search when switching tabs
            setSearchValue('');
            setDebouncedSearchText('');
          }}
          aria-label='user selection tabs'
          sx={tabsSx(theme, isDarkMode)}
        >
          <Tab label={t('admin.centralReportsPage.publishReport.individuals')} value='1' />
          <Tab label={t('admin.centralReportsPage.publishReport.userRoles')} value='2' />
        </TabList>
      </Box>
      <Grid container direction='row' justifyContent='space-between' sx={tabPanelSx}>
        <Grid item key='left-panel' sm={12} md={6} sx={customReportTabGridSx(theme, isDarkMode, true)}>
          {tabValue === '1' && (
            <>
              <SearchBar
                sx={searchBarInlineSx}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                showClearIcon={searchValue?.length > 0}
                debouncedSearchFn={debounceSearchFn}
              />
              {hasNoIndividualsResults && (
                <Box sx={noSearchResultsSx}>{t('admin.centralReportsPage.publishReport.noSearchResults')}</Box>
              )}
              {filteredIndividualsOptions?.map((user, i) => (
                <UserSelectionAccordion
                  key={user.id}
                  userGroup={user}
                  index={i}
                  selectedOptions={selectedOptions}
                  debouncedSearchText={debouncedSearchText}
                  isAllSelected={isAllSelected}
                  toggleSelectAll={toggleSelectAll}
                  toggleOption={toggleOption}
                  getOptionName={getOptionName}
                  mapOptionsForSelectAll={mapOptionsForSelectAll}
                  selectAllLabel={t('admin.centralReportsPage.publishReport.selectAllIndividuals')}
                  showUserName
                />
              ))}
            </>
          )}
          {tabValue === '2' && (
            <>
              <SearchBar
                sx={searchBarInlineSx}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                showClearIcon={searchValue?.length > 0}
                debouncedSearchFn={debounceSearchFn}
              />
              {hasNoRolesResults && (
                <Box sx={noSearchResultsSx}>{t('admin.centralReportsPage.publishReport.noSearchResults')}</Box>
              )}
              {filteredRolesOptions?.map((group, i) => (
                <UserSelectionAccordion
                  key={group.id}
                  userGroup={group}
                  index={i}
                  selectedOptions={selectedOptions}
                  debouncedSearchText={debouncedSearchText}
                  isAllSelected={isAllSelected}
                  toggleSelectAll={toggleSelectAll}
                  toggleOption={toggleOption}
                  getOptionName={getOptionName}
                  mapOptionsForSelectAll={mapOptionsForSelectAll}
                  selectAllLabel={t('admin.centralReportsPage.publishReport.selectAllUserRoles')}
                />
              ))}
            </>
          )}
        </Grid>
        <Grid item key='right-panel' md={6} sm={12} sx={customReportTabGridSx(theme, isDarkMode, false)}>
          <SelectionList
            selectedItems={selectedItemsForList}
            onClearSelection={handleClearSelection}
            onRemoveItem={handleChipRemoval}
            sortByName={false}
            getChipIcon={getChipIcon}
            chipColor='secondary'
          />
        </Grid>
      </Grid>
    </TabContext>
  );
}

SelectUsers.defaultProps = {
  onOptionToggle: undefined,
  onSelectAllToggle: undefined,
  onRemoveItem: undefined,
  onClearAll: undefined,
};

export default SelectUsers;
